import { AnyHTMLorSVGElementEventMap } from '@fullstory/shared/eventmaps';

interface Q {
  contains(e: Node | OfOne): boolean;
  setAttr(k: string, v: string): Q;
  setCss(k: string, v: any, p?: string): Q;
  setCssTransform(xform: string): Q;
  setHtml(v: string): Q;
  setProp(k: string, v: any): Q;
  setText(v: string): Q;
  setVal(v: string): Q;
  on(name: string, f: (e: Event) => void, capture?: boolean): Q;
  off(name: string, f: (e: Event) => void, capture?: boolean): Q;
  remove(): Q;
}

export class OfOne implements Q {
  e: HTMLElement;

  constructor(node: Node) {
    this.e = node as HTMLElement;
  }

  set(e: HTMLElement): OfOne {
    this.e = e;
    return this;
  }

  selectOne(sel: string): OfOne | null {
    const e = this.e.querySelector(sel);
    return e ? new OfOne(e) : null;
  }

  selectMany(sel: string): OfMany {
    return new OfMany(this.e.querySelectorAll(sel));
  }

  focus(): OfOne {
    this.e.focus();
    return this;
  }

  blur(): OfOne {
    this.e.blur();
    return this;
  }

  click(): OfOne {
    this.e.click();
    return this;
  }

  hasFocus(): boolean {
    const doc = this.e.ownerDocument || document;
    return doc.activeElement == this.e;
  }

  contains(q: Node | OfOne): boolean {
    return contains(this.e, q instanceof OfOne ? q.e : q);
  }

  text(): string | null {
    return text(this.e);
  }

  innerText(): string {
    return innerText(this.e);
  }

  html(): string {
    return html(this.e);
  }

  setHtml(v: string): OfOne {
    setHtml(this.e, v);
    return this;
  }

  setText(v: string): OfOne {
    setText(this.e, v);
    return this;
  }

  hasAttr(k: string): boolean {
    return hasAttr(this.e, k);
  }

  attr(k: string): string {
    return attr(this.e, k);
  }

  setAttr(k: string, v: string): OfOne {
    setAttr(this.e, k, v);
    return this;
  }

  prop(k: string): any {
    return prop(this.e, k);
  }

  setProp(k: string, v: any): OfOne {
    setProp(this.e, k, v);
    return this;
  }

  val(): string {
    return val(this.e);
  }

  setVal(v: string): OfOne {
    setVal(this.e, v);
    return this;
  }

  css(k: string): string {
    return css(this.e, k);
  }

  setCss(k: string, v: any, p?: string): OfOne {
    setCss(this.e, k, v, p);
    return this;
  }

  setCssTransform(xform: string): OfOne {
    setCssTransform(this.e, xform);
    return this;
  }

  setCssAnimationFillmode(fillmode: string): OfOne {
    setCssAnimationFillmode(this.e, fillmode);
    return this;
  }

  on<EventName extends keyof AnyHTMLorSVGElementEventMap>(
    name: EventName,
    f: (e: AnyHTMLorSVGElementEventMap[EventName]) => void,
    capture?: boolean,
  ): OfOne {
    on(this.e, name, f, capture);
    return this;
  }

  off<EventName extends keyof AnyHTMLorSVGElementEventMap>(
    name: EventName,
    f: (e: AnyHTMLorSVGElementEventMap[EventName]) => void,
    capture?: boolean,
  ): OfOne {
    off(this.e, name, f, capture);
    return this;
  }

  child(): OfOne | null {
    const p = this.e.firstElementChild;
    return p ? new OfOne(p) : null;
  }

  children(): OfMany {
    return new OfMany(this.e.children);
  }

  parent(): OfOne | null {
    const p = this.e.parentElement;
    return p ? new OfOne(p) : null;
  }

  remove(): OfOne {
    remove(this.e);
    return this;
  }

  hasClass(c: string): boolean {
    return hasClass(this.e, c);
  }

  setClass(c: string): OfOne {
    setClass(this.e, c);
    return this;
  }

  addClass(...c: string[]): OfOne {
    addClass(this.e, ...c);
    return this;
  }

  removeClass(...c: string[]): OfOne {
    removeClass(this.e, ...c);
    return this;
  }

  toggleClass(c: string): OfOne {
    toggleClass(this.e, c);
    return this;
  }

  switchClass(c: string, on: boolean): OfOne {
    switchClass(this.e, c, on);
    return this;
  }

  append(o: Node | OfOne): OfOne {
    append(this.e, o);
    return this;
  }

  insert(o: Node | OfOne, before: Node | OfOne) {
    insert(this.e, o, before);
    return this;
  }

  appendText(text: string): OfOne {
    const doc = this.e.ownerDocument || document;
    append(this.e, doc.createTextNode(text));
    return this;
  }

  appendTo(o: Node | OfOne): OfOne {
    append(o, this.e);
    return this;
  }

  prepend(o: Node | OfOne): OfOne {
    prepend(this.e, o);
    return this;
  }

  prependTo(o: Node | OfOne): OfOne {
    prepend(o, this.e);
    return this;
  }

  bounds(): ClientRect {
    return this.e.getBoundingClientRect();
  }

  elem(): HTMLElement {
    return this.e;
  }

  offsetWidth(): number {
    return this.e.offsetWidth;
  }

  offsetHeight(): number {
    return this.e.offsetHeight;
  }

  offsetTop(): number {
    return this.e.offsetTop;
  }

  offsetLeft(): number {
    return this.e.offsetLeft;
  }
}

export class OfMany implements Q {
  e: HTMLElement[];

  constructor(e?: Array<HTMLElement | OfOne> | ArrayLike<HTMLElement> | NodeList | HTMLCollection) {
    if (e instanceof Array) {
      this.e = e.map(i => {
        if (i instanceof OfOne) {
          return i.e;
        }
        return i;
      });
    } else if ((e && e.length > 0) || e instanceof NodeList || e instanceof HTMLCollection) {
      this.e = [];
      for (let i = 0, n = e.length; i < n; i++) {
        this.e.push(e[i] as HTMLElement);
      }
    } else {
      this.e = [];
    }
  }

  selectMany(sel: string): OfMany {
    const all = new OfMany();
    this.eachQ(q => {
      q.selectMany(sel).e.forEach(e => {
        all.push(e);
      });
    });
    return all;
  }

  empty(): boolean {
    return this.e.length == 0;
  }

  size(): number {
    return this.e.length;
  }

  contains(c: Node | OfOne): boolean {
    for (const el of this.e) {
      if (contains(el, c)) {
        return true;
      }
    }
    return false;
  }

  setHtml(v: string): OfMany {
    this.e.forEach(e => {
      setHtml(e, v);
    });
    return this;
  }

  setText(v: string): OfMany {
    this.e.forEach(e => {
      setText(e, v);
    });
    return this;
  }

  setAttr(k: string, v: string): OfMany {
    this.e.forEach(e => {
      setAttr(e, k, v);
    });
    return this;
  }

  setProp(k: string, v: any): OfMany {
    this.e.forEach(e => {
      setProp(e, k, v);
    });
    return this;
  }

  setVal(v: string): OfMany {
    this.e.forEach(e => {
      setVal(e, v);
    });
    return this;
  }

  setCss(k: string, v: any, p?: string): OfMany {
    this.e.forEach(e => {
      setCss(e, k, v, p);
    });
    return this;
  }

  setCssTransform(xform: string): OfMany {
    this.e.forEach(e => {
      setCssTransform(e, xform);
    });
    return this;
  }

  on<EventName extends keyof AnyHTMLorSVGElementEventMap>(
    name: EventName,
    f: (e: AnyHTMLorSVGElementEventMap[EventName]) => void,
    capture?: boolean,
  ): OfMany {
    this.e.forEach(e => {
      if (e) {
        on(e, name, f, capture);
      }
    });
    return this;
  }

  off<EventName extends keyof AnyHTMLorSVGElementEventMap>(
    name: EventName,
    f: (e: AnyHTMLorSVGElementEventMap[EventName]) => void,
    capture?: boolean,
  ): OfMany {
    this.e.forEach(e => {
      if (e) {
        off(e, name, f, capture);
      }
    });
    return this;
  }

  remove(): OfMany {
    this.e.forEach(remove);
    return this;
  }

  setClass(c: string): OfMany {
    this.e.forEach(e => {
      setClass(e, c);
    });
    return this;
  }

  addClass(...c: string[]): OfMany {
    this.e.forEach(e => {
      addClass(e, ...c);
    });
    return this;
  }

  removeClass(...c: string[]): OfMany {
    this.e.forEach(e => {
      removeClass(e, ...c);
    });
    return this;
  }

  toggleClass(c: string): OfMany {
    this.e.forEach(e => {
      toggleClass(e, c);
    });
    return this;
  }

  switchClass(c: string, on: boolean): OfMany {
    this.e.forEach(e => {
      switchClass(e, c, on);
    });
    return this;
  }

  appendTo(o: Node | OfOne): OfMany {
    this.e.forEach(e => {
      append(o, e);
    });
    return this;
  }

  prependTo(o: Node | OfOne): OfMany {
    this.e.forEach(e => {
      prepend(o, e);
    });
    return this;
  }

  eachQ(f: (q: OfOne, i: number) => void): OfMany {
    this.e.forEach((e, i) => {
      const q = new OfOne(e);
      f(q, i);
    });
    return this;
  }

  push(o: HTMLElement | OfOne): OfMany {
    this.e.push(o instanceof OfOne ? o.e : o);
    return this;
  }

  oneAt(index: number): OfOne {
    return new OfOne(this.e[index]!);
  }
}

export function createOne(t: string, doc: Document = document): OfOne {
  return new OfOne(doc.createElement(t));
}

export function createMany(t: string, n: number): OfMany {
  const q = new OfMany();
  while (q.e.length < n) {
    q.push(createOne(t));
  }
  return q;
}

export function selectMany(sel: string, t?: Pick<ParentNode, 'querySelectorAll'>): OfMany {
  t = t || document;
  return new OfMany(t.querySelectorAll(sel));
}

export function selectOne(sel: string, t?: Pick<ParentNode, 'querySelector'>): OfOne | null {
  t = t || document;
  const r = t.querySelector(sel);
  return r ? new OfOne(r) : null;
}

export function body(): OfOne {
  return new OfOne(document.body);
}

export function head(): OfOne {
  return new OfOne(document.head);
}

export function html(e: Element): string {
  return e.innerHTML;
}

export function setHtml(e: Element, v: string) {
  e.innerHTML = v;
}

export function text(e: Node): string | null {
  return e.textContent;
}

export function innerText(e: HTMLElement): string {
  return e.innerText;
}

export function setText(e: Node, v: string) {
  e.textContent = v;
}

export function css(e: Element & ElementCSSInlineStyle, k: string): string {
  // Some customer can have a child element with an ID of "style" and will override the style property
  const lv = e.style.getPropertyValue ? e.style.getPropertyValue(k) : null;
  return lv ? lv : getComputedStyle(e).getPropertyValue(k);
}

export function setCss(e: HTMLElement, k: string, v: string | number | null, priority?: string) {
  if (typeof v == 'number') {
    v = v.toString();
    // TODO(nishanth): The following check likely isn't as exhaustive as it
    // should be; i.e., as written, we may be appending 'px' in situations where
    // we shouldn't really be.
    if (k != 'opacity' && k != 'z-index') {
      v += 'px';
    }
  }
  e.style.setProperty(k, v, priority || '');
}

export function setCssTransform(e: ElementCSSInlineStyle, xform: string) {
  e.style.setProperty('-webkit-transform', xform);
  e.style.setProperty('-moz-transform', xform);
  e.style.setProperty('-ms-transform', xform);
  e.style.setProperty('transform', xform);
}

export function setCssAnimationFillmode(e: ElementCSSInlineStyle, fillmode: string) {
  e.style.setProperty('-webkit-animation-fill-mode', fillmode);
  e.style.setProperty('-moz-animation-fill-mode', fillmode);
  e.style.setProperty('-ms-animation-fill-mode', fillmode);
  e.style.setProperty('animation-fill-mode', fillmode);
}

// An HTMLElement which may have arbitrary expando properties.
interface HTMLElementWithProperties extends HTMLElement {
  [property: string]: any;
}

export function prop(e: HTMLElement, k: string): any {
  const eWithProperties = e as HTMLElementWithProperties;
  return eWithProperties[k];
}

export function setProp(e: HTMLElement, k: string, v: any) {
  const eWithProperties = e as HTMLElementWithProperties;
  if (v == null) {
    delete eWithProperties[k];
  } else {
    eWithProperties[k] = v;
  }
}

export function hasAttr(e: Element, k: string): boolean {
  return e.hasAttribute(k);
}

export function attr(e: Element, k: string): string {
  const value = e.getAttribute(k);
  return value !== null ? value : '';
}

export function setAttr(e: Element, k: string, v: string) {
  if (v == null) {
    e.removeAttribute(k);
  } else {
    e.setAttribute(k, v);
  }
}

export function contains(e: Node | OfOne, child: Node | OfOne): boolean {
  const en: Node = e instanceof OfOne ? e.e : e,
    cn: Node = child instanceof OfOne ? child.e : child;
  return en.contains(cn);
}

export function val(e: HTMLElement): string {
  return prop(e, 'value');
}

export function setVal(e: HTMLElement, v: string) {
  setProp(e, 'value', v);
}

export function on<EventName extends keyof AnyHTMLorSVGElementEventMap>(
  e: EventTarget,
  n: EventName,
  f: (e: AnyHTMLorSVGElementEventMap[EventName]) => void,
  capture?: boolean,
) {
  f && e.addEventListener(n, f as (e: Event) => void, !!capture);
}

export function off<EventName extends keyof AnyHTMLorSVGElementEventMap>(
  e: EventTarget,
  n: string,
  f: (e: AnyHTMLorSVGElementEventMap[EventName]) => void,
  capture?: boolean,
) {
  e.removeEventListener(n, f as (e: Event) => void, !!capture);
}

export function hasClass(e: Element, c: string): boolean {
  if (e.classList) {
    return e.classList.contains(c);
  }

  if (!e.className) {
    return false;
  }
  return e.className.split(' ').indexOf(c) != -1;
}

export function setClass(e: Element, c: string) {
  e.className = c;
}

export function addClass(e: Element, ...c: string[]) {
  if (e.classList) {
    e.classList.add(...c);
    return;
  }

  if (!e.className) {
    // There are no current classes. Add the ones that were passed in.
    e.className = c.join(' ');
  } else {
    const classes = e.className.split(' ');
    for (const cn of c) {
      // Add classes that don't exist to the end.
      if (classes.indexOf(cn) === -1) {
        e.className += ' ' + c;
      }
    }
  }
}

export function removeClass(e: Element, ...c: string[]) {
  if (e.classList) {
    e.classList.remove(...c);
    return;
  }

  if (!e.className) {
    return; // No classes present.
  }

  const list = e.className.split(' ');
  for (const cn of c) {
    const idx = list.indexOf(cn);
    if (idx == -1) {
      continue; // c does not exist.
    }

    list.splice(idx, 1);
  }
  e.className = list.join(' ');
}

export function toggleClass(e: Element, c: string) {
  if (e.classList) {
    e.classList.toggle(c);
    return;
  }

  if (hasClass(e, c)) {
    removeClass(e, c);
  } else {
    addClass(e, c);
  }
}

export function switchClass(e: Element, c: string, on: boolean) {
  if (on) {
    addClass(e, c);
  } else {
    removeClass(e, c);
  }
}

export function remove(e: Node) {
  const p = e.parentElement;
  if (p) {
    p.removeChild(e);
  }
}

export function insert(e: Node | OfOne, o: Node | OfOne, b: Node | OfOne) {
  const en: Node = e instanceof OfOne ? e.e : e,
    on: Node = o instanceof OfOne ? o.e : o,
    bn: Node = b instanceof OfOne ? b.e : b;
  en.insertBefore(on, bn);
}

export function append(e: Node | OfOne, o: Node | OfOne) {
  const en: Node = e instanceof OfOne ? e.e : e,
    on: Node = o instanceof OfOne ? o.e : o;
  en.appendChild(on);
}

export function prepend(e: Node | OfOne, o: Node | OfOne) {
  const en: Node = e instanceof OfOne ? e.e : e,
    on: Node = o instanceof OfOne ? o.e : o;
  en.insertBefore(on, en.firstChild);
}
